import type { IconComponent } from '@infinex/ui-icons';
import { ConfidentialFillIcon } from '@infinex/ui-icons';
import { cn } from '@infinex/ui-styles';
import { WalletAvatar } from '@infinex/ui/components';

import { useWalletDisplayInfo } from '../../api';
import { CONFIDENTIAL_WALLET_SOURCE_ID } from '../../features/balances';
import { getCustodyIcon } from '../../utils/custodyIcon';
import type { AvatarIconOrEmojiValue } from '../settings';

type CustodyEmblemSize = 'xsmall' | 'small' | 'medium' | 'large' | 'extraLarge';

type CustodyEmblemProminence = 'high' | 'medium' | 'low';

export type CustodyEmblemProps = {
  icon: IconComponent;
  size?: CustodyEmblemSize;
  prominence?: CustodyEmblemProminence;
  className?: string;
  isDisabled?: boolean;
  /**
   * TODO: Bob remove this once the icon customization is done.
   */
  withoutBackground?: boolean;
};

const SIZE_CONFIG: Record<
  CustodyEmblemSize,
  {
    container: string;
    iconSize: 'medium' | 'small' | 'xsmall' | '2xsmall';
  }
> = {
  extraLarge: {
    container: 'size-[72px]',
    iconSize: 'medium',
  },
  large: {
    container: 'size-10',
    iconSize: 'small',
  },
  medium: {
    container: 'size-8',
    iconSize: 'xsmall',
  },
  small: {
    container: 'size-6',
    iconSize: '2xsmall',
  },
  xsmall: {
    container: 'size-5',
    iconSize: '2xsmall',
  },
};

const PROMINENCE_CONFIG: Record<
  CustodyEmblemProminence,
  {
    background: string;
    iconFill?: 'brand' | 'default';
    iconClassName?: string;
  }
> = {
  high: {
    background: 'bg-canvas',
    iconFill: 'brand',
  },
  medium: {
    background: 'bg-fill',
    iconFill: 'default',
  },
  // The grey member of the wallet-avatar palette: `WalletAvatar` normalises
  // every avatar color to oklch L 0.8 / C 0.25 and paints its circle with
  // that color at 12% — this is the same formula with chroma zeroed. The
  // icon inherits the full-opacity grey via currentColor.
  low: {
    background: 'text-[oklch(0.8_0_0)] bg-current/12',
  },
};

export const CustodyEmblemFromWalletId = ({
  walletId,
  size = 'large',
  prominence = 'high',
  className,
  isDisabled,
  withoutBackground,
}: Pick<
  CustodyEmblemProps,
  'size' | 'prominence' | 'className' | 'isDisabled' | 'withoutBackground'
> & {
  walletId: string | 'vault';
  isDisabled?: boolean; // not used for now
}) => {
  const { getWalletDisplayInfo } = useWalletDisplayInfo();
  const walletDisplayInfo = getWalletDisplayInfo(walletId);
  const Icon = walletDisplayInfo.icon;

  // The confidential wallet is not a walletsV2 wallet — its reserved source
  // id maps to the fixed violet identity instead of a resolved avatar.
  if (walletId === CONFIDENTIAL_WALLET_SOURCE_ID) {
    return <ConfidentialWalletEmblem size={size} className={className} />;
  }

  const avatar = walletDisplayInfo.avatar;
  // TODO: Bob — the avatar path doesn't support prominence or isDisabled yet
  if (avatar) {
    return (
      <CustodyEmblemFromDisplayInfo
        avatar={avatar}
        size={size}
        // WalletAvatar always paints its own circle, so "no background" is
        // expressed by overriding it to transparent
        className={cn(withoutBackground && 'bg-transparent', className)}
      />
    );
  }

  return (
    <CustodyEmblemInnerUI
      icon={Icon}
      size={size}
      prominence={prominence}
      className={className}
      isDisabled={isDisabled}
      withoutBackground={withoutBackground}
    />
  );
};

/**
 * The avatar-rendering half of {@link CustodyEmblemFromWalletId}: a resolved
 * wallet avatar (the user's chosen emoji, or icon + color) as a
 * `WalletAvatar`. For callers that source display info outside the
 * `useWalletDisplayInfo` hook (e.g. one dataset read across a mapped menu).
 */
export const CustodyEmblemFromDisplayInfo = ({
  avatar,
  size = 'large',
  className,
}: Pick<CustodyEmblemProps, 'size' | 'className'> & {
  avatar: AvatarIconOrEmojiValue;
}) => {
  if (avatar.type === 'emoji') {
    return (
      <WalletAvatar
        type="import"
        emoji={avatar.emoji}
        size={size}
        className={className}
      />
    );
  }

  return (
    <WalletAvatar
      type="import"
      icon={avatar.icon}
      color={avatar.color}
      size={size}
      className={className}
    />
  );
};

// If the icon is static and can't be configured by user, can use the following components instead

export const InfinexWalletEmblem = (
  props: Pick<CustodyEmblemProps, 'size' | 'prominence' | 'className'>
) => {
  return <CustodyEmblemInnerUI icon={getCustodyIcon('infinex')} {...props} />;
};

export const HotWalletEmblem = (
  props: Pick<CustodyEmblemProps, 'size' | 'prominence' | 'className'>
) => {
  return <CustodyEmblemInnerUI icon={getCustodyIcon('imported')} {...props} />;
};

/**
 * Fixed violet "highlight" identity for the confidential wallet, as on the
 * portfolio ConfidentialWalletCard and the funds-received ReceiveToMenu.
 */
export const ConfidentialWalletEmblem = ({
  size = 'large',
  className,
}: Pick<CustodyEmblemProps, 'size' | 'className'>) => {
  const sizeConfig = SIZE_CONFIG[size];
  return (
    <span
      className={cn(
        'bg-canvas flex shrink-0 items-center justify-center rounded-full',
        sizeConfig.container,
        className
      )}
    >
      <ConfidentialFillIcon fill="highlight" size={sizeConfig.iconSize} />
    </span>
  );
};

export const ExternalSignerWalletEmblem = (
  props: Pick<CustodyEmblemProps, 'size' | 'prominence' | 'className'>
) => {
  return (
    <CustodyEmblemInnerUI icon={getCustodyIcon('external-signer')} {...props} />
  );
};

/**
 * only use it internally
 */
export const CustodyEmblemInnerUI = ({
  icon,
  size = 'large',
  prominence = 'high',
  className,
  isDisabled = false,
  withoutBackground = false,
}: CustodyEmblemProps) => {
  const Icon = icon;
  const sizeConfig = SIZE_CONFIG[size];
  const prominenceConfig = PROMINENCE_CONFIG[prominence];

  const iconFill = isDisabled ? undefined : prominenceConfig.iconFill;
  const iconClassName = isDisabled ? undefined : prominenceConfig.iconClassName;

  if (withoutBackground) {
    return (
      <Icon
        fill={iconFill}
        size={sizeConfig.iconSize}
        className={iconClassName}
      />
    );
  }

  return (
    <div
      className={cn(
        'flex shrink-0 items-center justify-center rounded-full',
        sizeConfig.container,
        prominenceConfig.background,
        className
      )}
    >
      <Icon
        fill={iconFill}
        size={sizeConfig.iconSize}
        className={iconClassName}
      />
    </div>
  );
};
